import React from 'react';

/**
 * Lightweight, zero-dependency Markdown & Table parser for Admin AI responses.
 * Parses headers, bold, italics, inline code, lists, horizontal rules, and Markdown tables.
 */
export default function MarkdownRenderer({ content = '' }) {
  if (!content) return null;

  // Split by line and group into blocks (paragraphs, tables, lists, headers)
  const lines = content.split('\n');
  const elements = [];
  let i = 0;

  const parseInline = (text) => {
    // Regex replacements for inline elements
    // Split text by tokens to safely create React elements
    const parts = [];
    // Tokenize: code `...`, bold **...**, italic *...*
    const regex = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let lastIdx = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIdx) {
        parts.push(text.substring(lastIdx, match.index));
      }
      const token = match[0];
      if (token.startsWith('`') && token.endsWith('`')) {
        parts.push(
          <code
            key={match.index}
            style={{
              background: '#0d1117',
              color: '#58a6ff',
              padding: '2px 6px',
              borderRadius: '6px',
              fontSize: '12px',
              fontFamily: 'monospace',
              border: '1px solid #30363d',
            }}
          >
            {token.slice(1, -1)}
          </code>
        );
      } else if (token.startsWith('**') && token.endsWith('**')) {
        parts.push(
          <strong key={match.index} style={{ color: '#fff', fontWeight: '700' }}>
            {token.slice(2, -2)}
          </strong>
        );
      } else if (token.startsWith('*') && token.endsWith('*')) {
        parts.push(
          <em key={match.index} style={{ color: '#cbd5e1' }}>
            {token.slice(1, -1)}
          </em>
        );
      }
      lastIdx = regex.lastIndex;
    }
    if (lastIdx < text.length) {
      parts.push(text.substring(lastIdx));
    }
    return parts.length > 0 ? parts : text;
  };

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // 1. Horizontal Rule
    if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
      elements.push(
        <hr
          key={i}
          style={{
            border: 'none',
            borderTop: '1px solid #2d3748',
            margin: '18px 0',
          }}
        />
      );
      i++;
      continue;
    }

    // 2. Markdown Table Detection (Line starts and ends with | or contains multiple |)
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }

      if (tableLines.length >= 2) {
        const headerCells = tableLines[0]
          .slice(1, -1)
          .split('|')
          .map((c) => c.trim());
        
        // Skip separator row (e.g. |:---|:---:|)
        const rowStartIndex = tableLines[1].includes('---') ? 2 : 1;
        const bodyRows = tableLines.slice(rowStartIndex).map((r) =>
          r
            .slice(1, -1)
            .split('|')
            .map((c) => c.trim())
        );

        elements.push(
          <div
            key={`table-${i}`}
            style={{
              overflowX: 'auto',
              margin: '16px 0',
              borderRadius: '10px',
              border: '1px solid #2d3748',
              background: '#0d1117',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                textAlign: 'left',
                fontSize: '13px',
              }}
            >
              <thead>
                <tr style={{ background: '#161b22', borderBottom: '2px solid #30363d' }}>
                  {headerCells.map((h, hIdx) => (
                    <th
                      key={hIdx}
                      style={{
                        padding: '12px 14px',
                        color: '#58a6ff',
                        fontWeight: '700',
                        fontSize: '12px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {parseInline(h)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bodyRows.map((row, rIdx) => (
                  <tr
                    key={rIdx}
                    style={{
                      borderBottom: '1px solid #21262d',
                      background: rIdx % 2 === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.02)',
                    }}
                  >
                    {row.map((cell, cIdx) => (
                      <td
                        key={cIdx}
                        style={{
                          padding: '10px 14px',
                          color: '#e6edf3',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {parseInline(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        continue;
      }
    }

    // 3. Headings
    if (trimmed.startsWith('# ')) {
      elements.push(
        <h1 key={i} style={{ fontSize: '20px', fontWeight: '800', color: '#00d68f', margin: '20px 0 10px' }}>
          {parseInline(trimmed.slice(2))}
        </h1>
      );
      i++;
      continue;
    }
    if (trimmed.startsWith('## ')) {
      elements.push(
        <h2 key={i} style={{ fontSize: '17px', fontWeight: '700', color: '#38bdf8', margin: '18px 0 8px' }}>
          {parseInline(trimmed.slice(3))}
        </h2>
      );
      i++;
      continue;
    }
    if (trimmed.startsWith('### ')) {
      elements.push(
        <h3 key={i} style={{ fontSize: '15px', fontWeight: '700', color: '#facc15', margin: '14px 0 6px' }}>
          {parseInline(trimmed.slice(4))}
        </h3>
      );
      i++;
      continue;
    }
    if (trimmed.startsWith('#### ')) {
      elements.push(
        <h4 key={i} style={{ fontSize: '14px', fontWeight: '700', color: '#e2e8f0', margin: '12px 0 4px' }}>
          {parseInline(trimmed.slice(5))}
        </h4>
      );
      i++;
      continue;
    }

    // 4. Bullet lists (* or -)
    if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
      const listItems = [];
      while (i < lines.length && (lines[i].trim().startsWith('* ') || lines[i].trim().startsWith('- '))) {
        listItems.push(lines[i].trim().slice(2));
        i++;
      }
      elements.push(
        <ul key={`list-${i}`} style={{ margin: '8px 0 12px', paddingLeft: '20px', color: '#e2e8f0', lineHeight: '1.7' }}>
          {listItems.map((item, idx) => (
            <li key={idx} style={{ marginBottom: '4px' }}>
              {parseInline(item)}
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // 5. Numbered lists (1. , 2. )
    if (/^\d+\.\s/.test(trimmed)) {
      const listItems = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
        listItems.push(lines[i].trim().replace(/^\d+\.\s/, ''));
        i++;
      }
      elements.push(
        <ol key={`numlist-${i}`} style={{ margin: '8px 0 12px', paddingLeft: '20px', color: '#e2e8f0', lineHeight: '1.7' }}>
          {listItems.map((item, idx) => (
            <li key={idx} style={{ marginBottom: '4px' }}>
              {parseInline(item)}
            </li>
          ))}
        </ol>
      );
      continue;
    }

    // 6. Blockquote
    if (trimmed.startsWith('> ')) {
      elements.push(
        <blockquote
          key={i}
          style={{
            margin: '10px 0',
            padding: '10px 16px',
            borderLeft: '4px solid #00d68f',
            background: 'rgba(0, 214, 143, 0.05)',
            borderRadius: '0 8px 8px 0',
            color: '#e2e8f0',
            fontSize: '13px',
          }}
        >
          {parseInline(trimmed.slice(2))}
        </blockquote>
      );
      i++;
      continue;
    }

    // 7. Regular Paragraph
    if (trimmed.length > 0) {
      elements.push(
        <p key={i} style={{ margin: '6px 0', lineHeight: '1.6', color: '#e2e8f0' }}>
          {parseInline(trimmed)}
        </p>
      );
    }

    i++;
  }

  return <div style={{ wordBreak: 'break-word', fontSize: '14px' }}>{elements}</div>;
}
